import { Box, Chip, IconButton, TableCell, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If, Else } from "lib/@components/switch"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { confirm } from "lib/@dialogs/confirm"
import { StandardCombo } from "lib/@components/combos/standardCombo"
import { COLLABORATE_LICENSE, CREATE_LICENSE } from "library/license-types.mjs"
import { deleteScimGroupMapping } from "routes/settings/plugins/scim/controller/upsert-scim-group-mapping"
import { RolesPicker } from "./roles-picker"

// Mirror the Users-tab license dropdown: see lib/licenses/components/license-allocator.js.
const LICENSE_OPTIONS = ["none", CREATE_LICENSE, COLLABORATE_LICENSE]

function buildForm(m) {
    return { license: m?.license || "none", roleIds: m?.roles || [], priority: m?.priority ?? 0 }
}

function licenseLabel(value) {
    if (!value || value === "none") return "None"
    return value.charAt(0).toUpperCase() + value.slice(1)
}

export function MappingRow({ item: mapping }) {
    const { client, forms, roles, onFormChange } = useBoundContext()
    const clientId = client?.id
    const formValues = forms?.[mapping?._id] ?? buildForm(mapping)
    const { license, roleIds, priority } = formValues

    const primaryLabel = mapping.displayName || mapping.externalId || "(unset)"
    const secondaryLabel = mapping.displayName && mapping.externalId ? mapping.externalId : null

    return (
        <TableRow sx={mapping._stale ? { backgroundColor: "warning.lighter", opacity: 0.85 } : undefined}>
            <TableCell sx={{ verticalAlign: "middle" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2">{primaryLabel}</Typography>
                    <If value={mapping._stale}>
                        <Chip size="small" label="group gone" color="warning" variant="outlined" />
                    </If>
                </Box>
                <If value={!!secondaryLabel}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {secondaryLabel}
                    </Typography>
                </If>
            </TableCell>
            <TableCell>
                <StandardCombo
                    size="small"
                    options={LICENSE_OPTIONS}
                    value={license}
                    onChange={handleLicenseChange}
                    getOptionLabel={licenseLabel}
                />
            </TableCell>
            <TableCell>
                <If value={license === CREATE_LICENSE}>
                    <RolesPicker roles={roles} value={roleIds} onChange={handleRolesChange} />
                    <Else>
                        <Typography variant="body2" color="text.disabled" sx={{ px: 1 }}>
                            N/A
                        </Typography>
                    </Else>
                </If>
            </TableCell>
            <TableCell align="center">
                <TextField
                    size="small"
                    type="number"
                    value={priority}
                    onChange={handlePriorityChange}
                    sx={{ width: 70 }}
                />
            </TableCell>
            <TableCell align="right">
                <Tooltip title="Delete mapping">
                    <IconButton onClick={remove} size="small">
                        <Iconify icon="mdi:delete" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    )

    function handleLicenseChange(e) {
        const lic = e.target.value
        onFormChange(mapping._id, { license: lic, roleIds: lic === CREATE_LICENSE ? roleIds : [], priority })
    }

    function handleRolesChange(newRoleIds) {
        onFormChange(mapping._id, { license, roleIds: newRoleIds, priority })
    }

    function handlePriorityChange(e) {
        onFormChange(mapping._id, { license, roleIds, priority: Number(e.target.value) || 0 })
    }

    async function remove() {
        if (!(await confirm("Delete this mapping? Affected users will be reconciled.", "Delete mapping"))) {
            return
        }
        try {
            await deleteScimGroupMapping({ id: mapping._id, clientId })
            invalidate("getScimGroupMappings")
            successSnackbar("Mapping deleted — affected users reconciled")
        } catch (error) {
            errorSnackbar(error?.message || "Delete failed")
        }
    }
}
